import React, { useState } from 'react';
import { SessionRow } from './SessionRow';
import { ActivityLogRow } from './ActivityLogRow';
import { useTimerStore } from '../../stores/timerStore';
import { formatDuration } from '../../utils/formatTime';
import type { SessionEntry, ActivityLogEntry } from '../../types';

interface SessionHistoryPanelProps {
  history: Record<string, SessionEntry>;
  activityLog: Record<string, ActivityLogEntry>;
}

export function SessionHistoryPanel({
  history,
  activityLog,
}: SessionHistoryPanelProps) {
  const { isHistoryPanelOpen, setHistoryPanelOpen } = useTimerStore();
  const [activeTab, setActiveTab] = useState<'activity' | 'sessions'>('activity');

  const sessionEntries = Object.entries(history)
    .map(([id, entry]) => ({ ...entry, id }))
    .sort((a, b) => b.endedAt - a.endedAt);

  const activityEntries = Object.entries(activityLog)
    .map(([id, entry]) => ({ ...entry, id }))
    .sort((a, b) => b.timestamp - a.timestamp);

  const totalMs = sessionEntries.reduce((sum, e) => sum + e.totalDurationMs, 0);

  return (
    <div className="border-t border-namm-line/30 bg-namm-panel/60 backdrop-blur-md">
      {/* Toggle button */}
      <button
        onClick={() => setHistoryPanelOpen(!isHistoryPanelOpen)}
        className="w-full flex items-center justify-between px-5 py-3 text-xs font-mono uppercase tracking-widest text-namm-muted hover:text-namm-cyan transition-colors"
      >
        <div className="flex items-center gap-4">
          <span className="font-semibold">
            📊 Suivi & Historique
          </span>
          <span className="text-[11px] text-namm-cyan font-normal">
            {activityEntries.length} action{activityEntries.length > 1 ? 's' : ''} · {sessionEntries.length} session{sessionEntries.length > 1 ? 's' : ''}
          </span>
        </div>
        <span className="text-sm">{isHistoryPanelOpen ? '▲' : '▼'}</span>
      </button>

      {/* Panel content */}
      {isHistoryPanelOpen && (
        <div className="px-5 pb-5 pt-1">
          {/* Tabs bar */}
          <div className="flex items-center justify-between border-b border-namm-line/25 mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('activity')}
                className={`py-2 px-3 text-xs font-mono uppercase tracking-wider border-b-2 transition-all ${
                  activeTab === 'activity'
                    ? 'border-namm-cyan text-namm-cyan font-semibold'
                    : 'border-transparent text-namm-muted hover:text-namm-text'
                }`}
              >
                📜 Journal d'activité ({activityEntries.length})
              </button>
              <button
                onClick={() => setActiveTab('sessions')}
                className={`py-2 px-3 text-xs font-mono uppercase tracking-wider border-b-2 transition-all ${
                  activeTab === 'sessions'
                    ? 'border-namm-cyan text-namm-cyan font-semibold'
                    : 'border-transparent text-namm-muted hover:text-namm-text'
                }`}
              >
                ⏱ Sessions terminées ({sessionEntries.length})
              </button>
            </div>

            {/* Total tracked time */}
            {activeTab === 'sessions' && sessionEntries.length > 0 && (
              <div className="flex items-center gap-4">
                <span className="text-xs font-mono text-namm-muted">
                  Total : <span className="text-namm-cyan font-semibold">{formatDuration(totalMs)}</span>
                </span>
              </div>
            )}
          </div>

          {/* Tab 1: Activity Log */}
          {activeTab === 'activity' && (
            <div>
              {activityEntries.length === 0 ? (
                <p className="text-sm font-mono text-namm-muted text-center py-8">
                  Aucune action enregistrée pour le moment.
                  <br />
                  <span className="text-xs opacity-75">
                    Les démarrages, pauses, stops et modifications s'afficheront ici en direct.
                  </span>
                </p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {activityEntries.map((entry) => (
                    <ActivityLogRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Sessions History */}
          {activeTab === 'sessions' && (
            <div>
              {sessionEntries.length === 0 ? (
                <p className="text-sm font-mono text-namm-muted text-center py-8">
                  Aucune session enregistrée.
                  <br />
                  <span className="text-xs opacity-75">
                    Arrêtez un chronomètre pour sauvegarder une session.
                  </span>
                </p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {sessionEntries.map((entry) => (
                    <SessionRow
                      key={entry.id}
                      entry={entry}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
